import { z } from 'zod';

const priorConversationSchema = z.object({
  conversationId: z.string(),
  workspaceConversationId: z.number(),
  /** yyyy-MM-dd */
  date: z.string(),
  issue: z.string().nullable(),
  resolution: z.string().nullable(),
  /** absent on summaries stored before these fields existed */
  fixed: z.boolean().default(false),
  cited: z.boolean().default(true),
  matchedOn: z.array(z.string()),
});

const recurrenceSchema = z.object({
  pattern: z.string(),
  priorConversations: z.array(priorConversationSchema).min(1),
});

const storedSummarySchema = z.object({
  recurrence: recurrenceSchema.nullable().optional(),
});

export type ConversationRecurrence = z.infer<typeof recurrenceSchema>;

/**
 * Reads `recurrence` from a conversation summary's `intermediateSummary`. The backend has already
 * validated and scored the cited conversations; anything unreadable, or a summary written before
 * the field existed, reads as no recurrence.
 */
export const parseConversationRecurrence = (
  intermediateSummary?: string | null,
): ConversationRecurrence | null => {
  if (!intermediateSummary) {
    return null;
  }

  try {
    const parsed = storedSummarySchema.safeParse(JSON.parse(intermediateSummary));
    return parsed.success ? (parsed.data.recurrence ?? null) : null;
  } catch {
    return null;
  }
};
